/**
 * useFireworksCode - Hook to detect fireworks easter egg sequence
 * Sequence: ← → ← →
 */

import { useEffect, useRef, useCallback } from 'react';
import { FIREWORKS_CODE } from '../types/game';

export function useFireworksCode(onActivate: () => void, enabled = true): void {
  const keySequence = useRef<string[]>([]);
  const timeoutRef = useRef<number | null>(null);

  const resetSequence = useCallback(() => {
    keySequence.current = [];
  }, []);

  const handleKeyDown = useCallback((event: KeyboardEvent) => {
    if (!enabled) return;

    // Reset timeout
    if (timeoutRef.current) {
      window.clearTimeout(timeoutRef.current);
    }
    timeoutRef.current = window.setTimeout(resetSequence, 2000);

    keySequence.current.push(event.code);

    if (keySequence.current.length > FIREWORKS_CODE.length) {
      keySequence.current.shift();
    }

    if (keySequence.current.length === FIREWORKS_CODE.length) {
      const matches = FIREWORKS_CODE.every(
        (key, index) => keySequence.current[index] === key
      );

      if (matches) {
        onActivate();
        keySequence.current = [];
      }
    }
  }, [onActivate, enabled, resetSequence]);

  useEffect(() => {
    if (!enabled) return;

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      if (timeoutRef.current) {
        window.clearTimeout(timeoutRef.current);
      }
    };
  }, [handleKeyDown, enabled]);
}
